import * as React from 'react'

import uniqBy from 'lodash/uniqBy'
import { MobXProviderContext, observer } from 'mobx-react'

import {
  ILocationClosureInterval,
  IProjectClosedInterval,
  RestrictionType,
} from '~/client/graph'
import { useResource } from '~/client/src/desktop/components/Resource/context'
import useColumnConfig from '~/client/src/desktop/components/Resource/hooks/useColumnConfig'
import { useSelectionHasProjectLocation } from '~/client/src/desktop/components/Resource/hooks/useSelectionHasProjectLocation'
import { TableFiltersProps } from '~/client/src/desktop/components/TableFilters/types'
import { useTableGroupSort } from '~/client/src/desktop/components/TableGroupSort'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import type ProjectSetUpPageStore from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPage.store'
import { CollapseButton } from '~/client/src/shared/components/CollapseButton/CollapseButton'
import { useCompanyListSelectorData } from '~/client/src/shared/components/CompanyListSelector'
import { IMeCompanyQuickFiltersProps } from '~/client/src/shared/components/Filters/MeCompanyQuickFilters/MeCompanyQuickFilters'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import { normalizeAllToEmpty } from '~/client/src/shared/components/ResourceControls/controlKeys'
import { IAllowanceEdit } from '~/client/src/shared/components/ResourceControls/resourceFieldAllowanceWrites'
import { useBulkReplaceResourceFieldAllowances } from '~/client/src/shared/components/ResourceControls/useSaveResourceFieldAllowances'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { IViewOptionsPanelProps } from '~/client/src/shared/components/ViewOptions'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { useResourceControls } from '~/client/src/shared/hooks/useResourceControls'
import { useGroupingControls } from '~/client/src/shared/hooks/useTableGrouping'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Closure from '~/client/src/shared/models/Closure'
import UserProject from '~/client/src/shared/models/UserProject'
import type {
  IProposedResourceControls,
  IResourceControlCascade,
  IWorkflowRestrictionDraft,
} from '~/client/src/shared/resources/domain/resourceControls'
import { collectGroupPaths } from '~/client/src/shared/resources/grouping/groupPath'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { toast } from '~/client/src/shared/utils/toast'

import { getControlsGroupPaths } from './getControlsGroupPaths'
import { navigateToProjectTimeSettings } from './navigateToProjectTimeSettings'
import ResourceControlCascadeConfirmDialog from './ResourceControlCascadeConfirmDialog'
import {
  FROZEN_COLUMN_KEYS,
  getResourceControlsColumns,
  PERMITTED_COMPANIES_KEY,
  RESOURCE_CONTROL_KEYS,
  SHOW_IN_MAP_KEY,
  WORKFLOW_RESOURCE_KEY,
} from './resourceControlsColumns'
import ResourceControlsSelectionBar from './ResourceControlsSelectionBar'
import ResourceControlsTable from './ResourceControlsTable'
import ResourceControlsToolbar from './ResourceControlsToolbar'
import { IResourceControlsConfig } from './types'
import {
  CONTROL_FACET_KEYS,
  useResourceControlsFilters,
} from './useResourceControlsFilters'

const EMPTY_PROJECT_CLOSURES: IProjectClosedInterval[] = []

// Empty-last string comparison for the control sort keys (name, stxResourceId),
// matching the default applySorting behavior they used before a comparator was
// supplied.
function compareControlValues(a: any, b: any, order: SortOrder): number {
  const isEmpty = (value: any) =>
    value == null || (typeof value === 'string' && value.trim() === '')
  const aEmpty = isEmpty(a)
  const bEmpty = isEmpty(b)
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  const cmp = String(a).toLowerCase().localeCompare(String(b).toLowerCase())
  return order === SortOrder.ASC ? cmp : -cmp
}

interface ProvidedStores {
  companiesStore: CompaniesStore
  projectTradesStore: ProjectTradesStore
  userProjectsStore: UserProjectsStore
  permitTypesStore: WorkflowTypesStore
  closuresStore: ClosuresStore
  projectDateStore: ProjectDateStore
  projectSetUpPageStore: ProjectSetUpPageStore
  state: DesktopInitialState
}

interface IProps {
  projectId: string
  userId: string
  config: IResourceControlsConfig
}

function ResourceControlsContent({ projectId, userId, config }: IProps) {
  const { controlCommands, columnConfigKey, labels, ariaLabel } = config
  const controls = config.controls ?? RESOURCE_CONTROL_KEYS

  const {
    companiesStore,
    projectTradesStore,
    userProjectsStore,
    permitTypesStore,
    closuresStore,
    projectDateStore,
    projectSetUpPageStore,
    state,
  } = React.useContext(MobXProviderContext) as unknown as ProvidedStores

  const { state: resourceState, actions, meta } = useResource()
  const hasProjectLocationSelected = useSelectionHasProjectLocation()
  // Stable identity: the array is read straight off the project, and an inline `?? []`
  // would hand the columns memo a fresh one on every render.
  const projectClosedIntervals =
    state?.activeProject?.closedIntervals ?? EMPTY_PROJECT_CLOSURES

  // Opens the project settings Time tab where working hours are edited — the
  // destination for the inherited-hours chip link in the Hours-of-operation cell.
  const onEditProjectHours = React.useCallback(
    () => navigateToProjectTimeSettings(projectSetUpPageStore),
    [projectSetUpPageStore],
  )

  const saveClosure = React.useCallback(
    (
      recordId: string,
      closureInterval: ILocationClosureInterval,
      closureId?: string,
    ) => {
      const closure = Closure.fromDto({
        id: closureId,
        locationId: recordId,
        projectId,
        closureInterval,
      })
      return closuresStore.saveClosures([closure])
    },
    [closuresStore, projectId],
  )

  const removeClosure = React.useCallback(
    async (closureId: string) => {
      try {
        await closuresStore.deleteClosures([closureId])
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [closuresStore],
  )

  const deleteRecord = React.useCallback(
    (recordId: string) => actions.deleteRecords([recordId]),
    [actions],
  )

  const deleteSelected = React.useCallback(async () => {
    await actions.deleteRecords(Array.from(resourceState.selectedIds))
    actions.clearSelection()
  }, [actions, resourceState.selectedIds])

  const { companies, companyMetadata } = useCompanyListSelectorData({
    companiesStore,
    projectTradesStore,
  })

  const initialColumns = React.useMemo(
    () => getResourceControlsColumns(controls),
    [controls],
  )
  const {
    configuredColumns,
    defaultColumns,
    updateColumnsConfiguration,
    resetColumnsConfiguration,
  } = useColumnConfig({
    initialColumns,
    projectId,
    userId,
    storageKey: columnConfigKey,
    defaultFrozenDataKeys: FROZEN_COLUMN_KEYS,
  })

  // Grouping by the Permitted Companies, Show in Maps and WR control fields, plus
  // every groupable data property the Objects tab offers (from the provider).
  const groupingOptions = React.useMemo(
    (): IOptionItem[] => [
      {
        key: PERMITTED_COMPANIES_KEY,
        label: Localization.translator.permittedCompanies,
        glyph: 'company',
      },
      ...(controls.includes(SHOW_IN_MAP_KEY)
        ? [
            {
              key: SHOW_IN_MAP_KEY,
              label: Localization.translator.showInMap,
              glyph: 'checkbox',
            } satisfies IOptionItem,
          ]
        : []),
      {
        key: WORKFLOW_RESOURCE_KEY,
        label: Localization.translator.workflowResource,
        glyph: 'checkbox',
      },
      ...meta.groupingOptions,
    ],
    [controls, meta.groupingOptions],
  )
  // Name + ID, plus every sortable data property (deduping any the provider also lists).
  const sortingOptions = React.useMemo(() => {
    const controlSorts: IOptionItem[] = [
      {
        key: 'name',
        label: Localization.translator.name,
        glyph: 'name',
      },
      {
        key: 'stxResourceId',
        label: Localization.translator.id,
        glyph: 'id',
      },
      ...(controls.includes(SHOW_IN_MAP_KEY)
        ? [
            {
              key: SHOW_IN_MAP_KEY,
              label: Localization.translator.showInMap,
              glyph: 'checkbox',
            } satisfies IOptionItem,
          ]
        : []),
      {
        key: WORKFLOW_RESOURCE_KEY,
        label: Localization.translator.workflowResource,
        glyph: 'checkbox',
      },
    ]
    const controlIds = new Set(controlSorts.map(option => option.key))
    return [
      ...controlSorts,
      ...meta.sortingOptions.filter(option => !controlIds.has(option.key)),
    ]
  }, [controls, meta.sortingOptions])

  // applySorting routes ALL keys through sortRecordsByKey when present, so the
  // control sort keys are handled explicitly (preserving prior behavior) and
  // data-property keys are delegated to the provider's field projector.
  const sortRecordsByKey = React.useCallback(
    (records: any[], key: string, order: SortOrder) => {
      if (key === 'name' || key === 'stxResourceId') {
        return [...records].sort((a, b) =>
          compareControlValues(a[key], b[key], order),
        )
      }
      // WR (isBookable) and Show in Maps are control fields on `root`, not
      // projector fields; compare as booleans (off < on ascending), matching the
      // checkbox convention. Show in Maps defaults to shown when unset.
      if (key === WORKFLOW_RESOURCE_KEY) {
        return [...records].sort((a, b) => {
          const cmp =
            Number(a.root?.isBookable ?? false) -
            Number(b.root?.isBookable ?? false)
          return order === SortOrder.ASC ? cmp : -cmp
        })
      }
      if (key === SHOW_IN_MAP_KEY) {
        return [...records].sort((a, b) => {
          const cmp =
            Number(a.root?.showInMap ?? true) -
            Number(b.root?.showInMap ?? true)
          return order === SortOrder.ASC ? cmp : -cmp
        })
      }
      return meta.fieldProjector.sortRecords(records, key, order)
    },
    [meta.fieldProjector],
  )

  const { groupSortProps, currentGrouping, applySorting } = useTableGroupSort({
    groupingOptions,
    sortingOptions,
    initialSortKey: 'name',
    initialSortOrder: SortOrder.ASC,
    sortRecordsByKey,
  })
  const groupingControls = useGroupingControls()
  const groupingHierarchy = currentGrouping
    ? meta.fieldCatalog.getField(currentGrouping)?.hierarchy
    : undefined

  // Collapsed-group ids are scoped to the active grouping; clear them when the
  // grouping key changes so stale keys don't leak into the new grouping.
  const { reset: resetCollapsed } = groupingControls
  React.useEffect(() => {
    resetCollapsed()
  }, [currentGrouping, resetCollapsed])

  const {
    savePermittedCompanies,
    saveBookable,
    saveShowInMap,
    saveOperatingIntervals,
    disableAndClearControls,
    saveControls,
    previewControlCascade,
  } = useResourceControls(projectId, controlCommands)

  // Blast-radius confirmation for changes that make a resource invalid for a
  // workflow type (disable, or workflow-type narrowing). The handler previews
  // the cascade; a zero-affected result commits directly, otherwise this dialog
  // names the affected permit types and commits on confirm.
  const [cascadeDialog, setCascadeDialog] = React.useState<{
    cascade: IResourceControlCascade
    commit: () => Promise<void>
    resolve: () => void
  } | null>(null)
  const [isCommittingCascade, setIsCommittingCascade] = React.useState(false)
  // Guards the single cascadeDialog slot: true while a confirmation is open so a
  // concurrent toggle can't overwrite it and orphan the first awaiting promise.
  const cascadePendingRef = React.useRef(false)

  // Surfaces how many resources the server skipped so a partial bulk update
  // isn't silent.
  const notifySkippedResources = React.useCallback(
    (
      result: IResourceControlCascade | null,
      message: (count: number) => string = Localization.translator
        .someResourcesCouldNotBeDisabled,
    ) => {
      const skipped = result?.failedLocationIds?.length ?? 0
      if (skipped > 0) {
        toast.error(message(skipped))
      }
    },
    [],
  )

  const runWithCascadeConfirmation = React.useCallback(
    async (
      locationIds: string[],
      proposed: IProposedResourceControls,
      commit: () => Promise<void>,
    ) => {
      let cascade: IResourceControlCascade | null = null
      try {
        cascade = await previewControlCascade(locationIds, proposed)
      } catch {
        toast.error(Localization.translator.couldNotCheckAffectedWorkflows)
        return
      }

      // affectedPermitTypeCount is the authoritative count (the affectedPermitTypes
      // list is nullable and capped); confirm whenever it's non-zero.
      const affectedCount = cascade?.affectedPermitTypeCount ?? 0
      if (affectedCount === 0) {
        try {
          await commit()
        } catch {
          toast.error(Localization.translator.failedToSaveChangesTryAgain)
        }
        return
      }

      // Only one cascade confirmation fits the single dialog slot. If one is
      // already open, drop this request WITHOUT committing rather than overwrite
      // it — overwriting would orphan the open dialog's promise and hang its
      // awaiting toggle forever. The row unwinds; the user can retry once the
      // open dialog is resolved.
      if (cascadePendingRef.current) return
      cascadePendingRef.current = true

      // Hold the caller's promise — so the cell's in-flight state persists and the
      // toggle doesn't flicker back — until the admin confirms or cancels.
      const confirmedCascade = cascade
      try {
        await new Promise<void>(resolve =>
          setCascadeDialog({ cascade: confirmedCascade, commit, resolve }),
        )
      } finally {
        cascadePendingRef.current = false
      }
    },
    [previewControlCascade],
  )

  const onConfirmCascade = React.useCallback(async () => {
    if (!cascadeDialog) return
    setIsCommittingCascade(true)
    try {
      await cascadeDialog.commit()
    } catch {
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
    } finally {
      setIsCommittingCascade(false)
      cascadeDialog.resolve()
      setCascadeDialog(null)
    }
  }, [cascadeDialog])

  const onCancelCascade = React.useCallback(() => {
    if (isCommittingCascade) return
    cascadeDialog?.resolve()
    setCascadeDialog(null)
  }, [cascadeDialog, isCommittingCascade])

  const recordsById = React.useMemo(() => {
    const map = new Map<string, (typeof resourceState.records)[number]>()
    for (const record of resourceState.records) map.set(record.id, record)
    return map
  }, [resourceState.records])

  const getRecordRoot = React.useCallback(
    (id: string) => recordsById.get(id)?.root,
    [recordsById],
  )

  const isRecordBookable = React.useCallback(
    (id: string) => getRecordRoot(id)?.isBookable ?? false,
    [getRecordRoot],
  )

  const getRestrictionDrafts = React.useCallback(
    (root: any): IWorkflowRestrictionDraft[] => {
      const restrictions = root?.workflowRestrictions
      return [
        ...(restrictions?.maxBookingDurations ?? []),
        ...(restrictions?.maxOverlappingBookings ?? []),
      ].map(restriction => ({
        id: restriction.id ?? undefined,
        restrictionType: restriction.restrictionType,
        values: (restriction.values ?? []).flatMap((value: unknown) =>
          typeof value === 'string' ? [value] : [],
        ),
        workflowIds: (restriction.workflowIds ?? []).flatMap((id: unknown) =>
          typeof id === 'string' ? [id] : [],
        ),
      }))
    },
    [],
  )

  const disableResources = React.useCallback(
    async (ids: readonly string[]) => {
      const closureIdsByRecord = new Map(
        ids.map(id => [
          id,
          closuresStore
            .getLocationClosures(id)
            .filter(closure => !closure.associatedPermitId)
            .map(closure => closure.id),
        ]),
      )
      const result = await disableAndClearControls([...ids])
      const failedIds = new Set(result?.failedLocationIds ?? [])
      const closureIds = ids.flatMap(id =>
        failedIds.has(id) ? [] : (closureIdsByRecord.get(id) ?? []),
      )
      closuresStore.receiveDeleted(closureIds)
      return result
    },
    [closuresStore, disableAndClearControls],
  )

  // Assigning a value to a control turns the WR toggle on for any currently-off
  // rows (persisted). Used by both per-row and bulk save paths.
  const enableManyIfAssigning = React.useCallback(
    (ids: string[], hasValue: boolean): Promise<void> => {
      if (!hasValue) return Promise.resolve()
      const offIds = ids.filter(id => !isRecordBookable(id))
      return offIds.length ? saveBookable(offIds, true) : Promise.resolve()
    },
    [isRecordBookable, saveBookable],
  )

  const enableIfAssigning = React.useCallback(
    (id: string, hasValue: boolean) => enableManyIfAssigning([id], hasValue),
    [enableManyIfAssigning],
  )

  // Assigning workflow types to an off row turns it on, the same rule the companies, hours
  // and closures edits follow.
  const handleEnableBookable = React.useCallback(
    (id: string) => enableIfAssigning(id, true),
    [enableIfAssigning],
  )

  // Bulk Workflow Types REPLACES: the selection becomes allowed on exactly the ticked
  // fields and denied on every other. See useBulkReplaceResourceFieldAllowances.
  const bulkReplaceAllowances = useBulkReplaceResourceFieldAllowances()

  const bulkSaveWorkflowTypes = React.useCallback(
    async (edits: IAllowanceEdit[]) => {
      const ids = Array.from(resourceState.selectedIds)
      // Allowed implies bookable, so assigning turns WR on for the rows that are off.
      // Applying with NOTHING ticked is a revoke, not an assignment, and enables nothing.
      const offIds = edits.some(edit => edit.shouldAllow)
        ? ids.filter(id => !isRecordBookable(id))
        : []
      try {
        // Enable first, unlike the other bulk actions: enabling denies the rows on every
        // restricted field, and the allowances written after it must land on top of that,
        // or the denial would undo the fields the user just ticked. If the allowance write
        // then fails, the rows are bookable only where any newly enabled resource would be.
        //
        // Awaited, both of them: the popover closes and the selection clears on the way
        // out, so a failure has to surface before that rather than after.
        if (offIds.length) await saveBookable(offIds, true)
        await bulkReplaceAllowances(ids, edits, {
          resourceTypeId: meta.resourceTypeId,
          recordIds: offIds,
        })
        actions.clearSelection()
      } catch (error) {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
        // Rethrown, unlike the sibling actions: this editor opens EMPTY and replaces every
        // listed field, so its draft cannot be reconstructed from what is on screen after
        // it closes. runApply keeps the popover open when an apply throws.
        throw error
      }
    },
    [
      isRecordBookable,
      saveBookable,
      bulkReplaceAllowances,
      meta.resourceTypeId,
      resourceState.selectedIds,
      actions,
    ],
  )

  const bulkSavePermittedCompanies = React.useCallback(
    async (companyIds: string[]) => {
      const ids = Array.from(resourceState.selectedIds)
      try {
        // Enable check keys off the original (pre-normalization) selection.
        await savePermittedCompanies(
          ids,
          normalizeAllToEmpty(companyIds, companies),
        )
        await enableManyIfAssigning(ids, companyIds.length > 0)
        actions.clearSelection()
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [
      savePermittedCompanies,
      resourceState.selectedIds,
      enableManyIfAssigning,
      actions,
      companies,
    ],
  )

  const bulkSaveOperatingIntervals = React.useCallback(
    async (intervals: IMsDateInterval[]) => {
      const ids = Array.from(resourceState.selectedIds)
      try {
        // saveOperatingIntervals replaces the row's interval array, so passing
        // the staged list applies replace semantics directly.
        await saveOperatingIntervals(ids, intervals)
        await enableManyIfAssigning(ids, intervals.length > 0)
        actions.clearSelection()
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [
      saveOperatingIntervals,
      resourceState.selectedIds,
      enableManyIfAssigning,
      actions,
    ],
  )

  // Replace each selected row's closures with the staged set: drop existing
  // non-permit closures (permit-linked ones are preserved), then add the staged
  // intervals. The delete is awaited before the save so they don't race.
  const bulkSaveClosures = React.useCallback(
    async (closureIntervals: ILocationClosureInterval[]) => {
      const ids = Array.from(resourceState.selectedIds)
      try {
        const existing = ids.flatMap(id =>
          closuresStore
            .getLocationActiveClosures(id)
            .filter(closure => !closure.associatedPermitId),
        )
        const closures = ids.flatMap(locationId =>
          closureIntervals.map(closureInterval =>
            Closure.fromDto({ locationId, projectId, closureInterval }),
          ),
        )
        await closuresStore.replaceClosures(existing, closures)

        await enableManyIfAssigning(ids, closureIntervals.length > 0)
        actions.clearSelection()
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [
      resourceState.selectedIds,
      projectId,
      closuresStore,
      enableManyIfAssigning,
      actions,
    ],
  )

  // Replace one restriction type atomically per selected resource while
  // preserving that resource's restrictions of the other type.
  const bulkSaveRestrictions = React.useCallback(
    async (
      restrictionType: RestrictionType,
      drafts: IWorkflowRestrictionDraft[],
    ) => {
      const ids = Array.from(resourceState.selectedIds)
      try {
        const results = await Promise.all(
          ids.map(id =>
            saveControls([id], {
              isBookable: drafts.length > 0 ? true : isRecordBookable(id),
              workflowRestrictions: [
                ...getRestrictionDrafts(getRecordRoot(id)).filter(
                  restriction =>
                    restriction.restrictionType !== restrictionType,
                ),
                ...drafts,
              ],
            }),
          ),
        )
        for (const result of results) {
          notifySkippedResources(
            result,
            Localization.translator.someResourcesCouldNotBeUpdated,
          )
        }
        actions.clearSelection()
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [
      resourceState.selectedIds,
      saveControls,
      getRestrictionDrafts,
      getRecordRoot,
      notifySkippedResources,
      isRecordBookable,
      actions,
    ],
  )

  // Bulk WR toggle. Enabling is a plain bookable update. Disabling clears all
  // dependent controls atomically, gated by the blast-radius confirmation.
  const bulkSaveBookable = React.useCallback(
    async (isBookable: boolean) => {
      const ids = Array.from(resourceState.selectedIds)
      if (isBookable) {
        try {
          await saveBookable(ids, true)
          actions.clearSelection()
        } catch {
          toast.error(Localization.translator.failedToSaveChangesTryAgain)
        }
        return
      }
      await runWithCascadeConfirmation(ids, { isBookable: false }, async () => {
        const result = await disableResources(ids)
        actions.clearSelection()
        notifySkippedResources(result)
      })
    },
    [
      saveBookable,
      disableResources,
      resourceState.selectedIds,
      actions,
      runWithCascadeConfirmation,
      notifySkippedResources,
    ],
  )

  // Bulk "Show in Maps" — an independent display flag: a plain save across the
  // selection with no WR enable/clear cascade, mirroring the per-row toggle.
  const bulkSaveShowInMap = React.useCallback(
    async (showInMap: boolean) => {
      const ids = Array.from(resourceState.selectedIds)
      try {
        await saveShowInMap(ids, showInMap)
        actions.clearSelection()
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [saveShowInMap, resourceState.selectedIds, actions],
  )

  // Per-row control saves mirror the bulk paths: await the control mutation,
  // then the WR auto-enable, inside a try/catch — so a failed control save
  // surfaces a toast and never leaves a row enabled without its value.
  const handleSavePermittedCompanies = React.useCallback(
    async (id: string, companyIds: string[]) => {
      try {
        // Enable check keys off the original (pre-normalization) selection.
        await savePermittedCompanies(
          [id],
          normalizeAllToEmpty(companyIds, companies),
        )
        await enableIfAssigning(id, companyIds.length > 0)
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [savePermittedCompanies, enableIfAssigning, companies],
  )

  // Independent display flag: a plain save with no WR enable/clear cascade.
  const handleSaveShowInMap = React.useCallback(
    async (id: string, showInMap: boolean) => {
      try {
        await saveShowInMap([id], showInMap)
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [saveShowInMap],
  )

  const handleSaveOperatingIntervals = React.useCallback(
    async (id: string, intervals: IMsDateInterval[]) => {
      try {
        await saveOperatingIntervals([id], intervals)
        await enableIfAssigning(id, intervals.length > 0)
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [saveOperatingIntervals, enableIfAssigning],
  )

  const handleSaveWorkflowRestrictions = React.useCallback(
    async (id: string, drafts: IWorkflowRestrictionDraft[]) => {
      const hasValue = drafts.length > 0
      // Setting a restriction on an off row turns the resource on, so it serves what it
      // limits. Turning it on is cascade-previewed. While the row is on, keep the plain
      // save + auto-enable path.
      if (hasValue && !isRecordBookable(id)) {
        await runWithCascadeConfirmation(
          [id],
          { isBookable: true },
          async () => {
            const desired = [...getRestrictionDrafts(getRecordRoot(id))]
            for (const draft of drafts) {
              const index = draft.id
                ? desired.findIndex(restriction => restriction.id === draft.id)
                : -1
              if (index === -1) desired.push(draft)
              else desired[index] = draft
            }
            const result = await saveControls([id], {
              isBookable: true,
              workflowRestrictions: desired,
            })
            notifySkippedResources(
              result,
              Localization.translator.someResourcesCouldNotBeUpdated,
            )
          },
        )
        return
      }
      // The merged-set route, not saveWorkflowRestrictions, and the same one
      // handleDeleteWorkflowRestrictions takes. The dedicated mutation writes the restriction
      // documents alone, and its delta is reconciled client-side only for Equipment - the
      // Locations store has no applyWorkflowRestriction hook, so the row went stale until the
      // whole locations query re-ran. Writing through saveControls touches the location
      // document, whose subscription carries the fresh restrictions back to every store.
      try {
        const desired = [...getRestrictionDrafts(getRecordRoot(id))]
        for (const draft of drafts) {
          const index = draft.id
            ? desired.findIndex(restriction => restriction.id === draft.id)
            : -1
          if (index === -1) desired.push(draft)
          else desired[index] = draft
        }
        const result = await saveControls([id], {
          isBookable: isRecordBookable(id) || hasValue,
          workflowRestrictions: desired,
        })
        notifySkippedResources(
          result,
          Localization.translator.someResourcesCouldNotBeUpdated,
        )
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [
      saveControls,
      getRestrictionDrafts,
      getRecordRoot,
      notifySkippedResources,
      enableIfAssigning,
      isRecordBookable,
      permitTypesStore.actualTypeIds,
      runWithCascadeConfirmation,
    ],
  )

  const handleDeleteWorkflowRestrictions = React.useCallback(
    async (id: string, restrictionIds: string[]) => {
      try {
        const deletedIds = new Set(restrictionIds)
        const result = await saveControls([id], {
          isBookable: isRecordBookable(id),
          workflowRestrictions: getRestrictionDrafts(getRecordRoot(id)).filter(
            restriction => !deletedIds.has(restriction.id ?? ''),
          ),
        })
        notifySkippedResources(
          result,
          Localization.translator.someResourcesCouldNotBeUpdated,
        )
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [
      saveControls,
      isRecordBookable,
      getRestrictionDrafts,
      getRecordRoot,
      notifySkippedResources,
    ],
  )

  const handleSaveClosure = React.useCallback(
    async (
      recordId: string,
      closureInterval: ILocationClosureInterval,
      closureId?: string,
    ) => {
      try {
        await saveClosure(recordId, closureInterval, closureId)
        await enableIfAssigning(recordId, true)
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    },
    [saveClosure, enableIfAssigning],
  )

  // Per-row WR toggle. Enabling is a plain bookable update. Disabling clears
  // location controls and non-permit closures through their owning stores,
  // gated by the blast-radius confirmation dialog.
  const handleSaveBookable = React.useCallback(
    async (id: string, isBookable: boolean) => {
      if (isBookable) {
        try {
          await saveBookable([id], true)
        } catch {
          toast.error(Localization.translator.failedToSaveChangesTryAgain)
        }
        return
      }
      await runWithCascadeConfirmation(
        [id],
        { isBookable: false },
        async () => {
          const result = await disableResources([id])
          notifySkippedResources(result)
        },
      )
    },
    [
      saveBookable,
      disableResources,
      runWithCascadeConfirmation,
      notifySkippedResources,
    ],
  )

  // One universe for the seed, the collapse and facet/grouping expansion (R9) —
  // see `actualTypeIds`.
  const allWorkflowTypeIds = permitTypesStore.actualTypeIds
  const allCompanyIds = React.useMemo(
    () => companies.map(company => company.id),
    [companies],
  )
  // Seeds the Date-closures bulk editor with the de-duplicated union of the
  // selection's current values. A getter, not a pre-computed array, so the
  // per-closure date math stays out of this view's render path.
  const getDefaultClosures = React.useCallback(
    (): ILocationClosureInterval[] =>
      uniqBy(
        Array.from(resourceState.selectedIds).flatMap(id =>
          // Active closures only — mirrors what the Date closures column shows
          // (ResourceControlsTable uses getLocationActiveClosures) so we don't seed
          // outdated/expired closures the user can no longer see.
          closuresStore
            .getLocationActiveClosures(id)
            .filter(closure => !closure.associatedPermitId)
            .map(closure => closure.closureInterval),
        ),
        closure => `${closure.startDate}|${closure.endDate}|${closure.isDaily}`,
      ),
    [resourceState.selectedIds, closuresStore],
  )

  const {
    filterProps: controlFilterProps,
    applyFilters: applyControlsFilters,
  } = useResourceControlsFilters({
    records: resourceState.records,
    getCompanyById: companiesStore.getCompanyById,
    allCompanyIds,
    controls,
  })

  // Merge the control facets with the resource's data-property facets (the same
  // facets the Objects tab exposes via the provider). Control keys route to the
  // controls filter engine; property keys (and the "Me" quick filter) are applied
  // by the provider into resourceState.filteredRecords, with control facets
  // layered on top in sortedRecords.
  const filterProps = React.useMemo<TableFiltersProps>(
    () => ({
      facets: [...controlFilterProps.facets, ...meta.filterProps.facets],
      sourceMapByFacet: {
        ...controlFilterProps.sourceMapByFacet,
        ...meta.filterProps.sourceMapByFacet,
      },
      appliedFilters: {
        ...controlFilterProps.appliedFilters,
        ...meta.filterProps.appliedFilters,
      },
      onFilterApply: (key, ids) =>
        CONTROL_FACET_KEYS.has(key)
          ? controlFilterProps.onFilterApply(key, ids)
          : meta.filterProps.onFilterApply(key, ids),
      onResetAll: () => {
        controlFilterProps.onResetAll()
        meta.filterProps.onResetAll()
      },
      availableRecords: controlFilterProps.availableRecords,
      getOptionName: (id, key) =>
        CONTROL_FACET_KEYS.has(key)
          ? (controlFilterProps.getOptionName?.(id, key) ?? id)
          : meta.getOptionName(id, key),
    }),
    [controlFilterProps, meta.filterProps, meta.getOptionName],
  )

  // "My Company" is a shortcut that toggles the current user's company in the
  // Permitted Companies control facet (not a company data property), preserving
  // the original Controls behavior. "Me" stays the provider's property-based
  // quick filter (auto-disabled when the resource has no user property).
  const currentUserCompanyId = UserProject.getCompanyId(
    state?.user,
    userProjectsStore,
  )
  const isOnlyMyCompany = !!(
    currentUserCompanyId &&
    controlFilterProps.appliedFilters[PERMITTED_COMPANIES_KEY]?.has(
      currentUserCompanyId,
    )
  )
  const onToggleMyCompany = React.useCallback(() => {
    if (!currentUserCompanyId) return
    const next = new Set(
      controlFilterProps.appliedFilters[PERMITTED_COMPANIES_KEY] ?? [],
    )
    if (next.has(currentUserCompanyId)) {
      next.delete(currentUserCompanyId)
    } else {
      next.add(currentUserCompanyId)
    }
    controlFilterProps.onFilterApply(PERMITTED_COMPANIES_KEY, Array.from(next))
  }, [currentUserCompanyId, controlFilterProps])

  const quickFilterProps = React.useMemo<IMeCompanyQuickFiltersProps>(
    () => ({
      isOnlyMe: meta.quickFilters.isOnlyMe,
      isOnlyMyCompany,
      onToggleMe: meta.quickFilters.toggleOnlyMe,
      onToggleMyCompany,
      isMeDisabled: !meta.quickFilters.hasUserProperties,
      isMyCompanyDisabled: !currentUserCompanyId,
    }),
    [
      meta.quickFilters,
      isOnlyMyCompany,
      onToggleMyCompany,
      currentUserCompanyId,
    ],
  )

  const sortedRecords = React.useMemo(
    () =>
      applySorting(
        applyControlsFilters(resourceState.filteredRecords),
        configuredColumns,
      ),
    [
      applySorting,
      applyControlsFilters,
      resourceState.filteredRecords,
      configuredColumns,
    ],
  )
  const visibleColumns = React.useMemo(
    () => configuredColumns.filter(column => !column.isHidden),
    [configuredColumns],
  )

  // The group keys currently displayed — drives the collapse-all toggle. Mirrors
  // the table's grouping resolution via the shared helper.
  const groupDeps = React.useMemo(
    () => ({
      fieldProjector: meta.fieldProjector,
      getCompanyName: (id: string) => companiesStore.getCompanyById(id)?.name,
      allCompanyIds,
    }),
    [meta.fieldProjector, companiesStore, allCompanyIds],
  )
  const groupKeys = React.useMemo(() => {
    if (!currentGrouping) return []
    const keys = new Set<string>()
    sortedRecords.forEach(record => {
      const paths = getControlsGroupPaths(
        record,
        currentGrouping,
        groupDeps,
        groupingHierarchy,
      )
      collectGroupPaths(paths, currentGrouping).forEach(path => keys.add(path))
    })
    return Array.from(keys)
  }, [currentGrouping, sortedRecords, groupDeps, groupingHierarchy])

  const isAllCollapsed =
    groupKeys.length > 0 &&
    groupKeys.every(key => groupingControls.collapsedCategories.get(key))
  const onToggleCollapseAll = React.useCallback(() => {
    if (isAllCollapsed) {
      groupingControls.expandAll(groupKeys)
    } else {
      groupingControls.collapseAll(groupKeys)
    }
  }, [isAllCollapsed, groupKeys, groupingControls])

  const collapseToggle =
    currentGrouping && groupKeys.length > 0 ? (
      <CollapseButton
        isExpanded={!isAllCollapsed}
        onClick={onToggleCollapseAll}
        ariaLabel={
          isAllCollapsed
            ? Localization.translator.expandAll
            : Localization.translator.collapseAll
        }
      />
    ) : null

  const viewOptionsProps = React.useMemo<IViewOptionsPanelProps>(
    () => ({
      grouping: {
        value: groupSortProps.selectedGroupByKey,
        options: groupingOptions,
        defaultValue: groupSortProps.initialGroupByKey,
        onChange: groupSortProps.onGroupingChange,
      },
      sorting: {
        value: {
          key: groupSortProps.selectedSortingOptionId || null,
          order: groupSortProps.selectedSortOrder,
        },
        defaultValue: {
          key: groupSortProps.initialSortKey,
          order: groupSortProps.initialSortOrder,
        },
        options: sortingOptions,
        onChange: value =>
          groupSortProps.onSortingChange(
            value.key ?? '',
            value.order ?? SortOrder.DEFAULT,
          ),
      },
      columns: {
        items: configuredColumns.map(column => ({
          key: column.dataKey,
          label: column.label ?? '',
          isFixed: column.isFixed,
          isHidden: column.isHidden,
        })),
        defaultItems: defaultColumns.map(column => ({
          key: column.dataKey,
          label: column.label ?? '',
          isFixed: column.isFixed,
          isHidden: column.isHidden,
        })),
        onChange: items =>
          updateColumnsConfiguration(
            items.map(item => ({
              dataKey: item.key,
              isFixed: !!item.isFixed,
              isHidden: !!item.isHidden,
            })),
          ),
        onReset: resetColumnsConfiguration,
      },
    }),
    [
      groupSortProps,
      groupingOptions,
      sortingOptions,
      configuredColumns,
      defaultColumns,
      updateColumnsConfiguration,
      resetColumnsConfiguration,
    ],
  )

  return (
    <div className="tw:relative tw:flex tw:h-full tw:min-h-0 tw:flex-col">
      <ResourceControlsToolbar
        searchKey={resourceState.searchKey}
        onSearchChange={actions.setSearchKey}
        filterProps={filterProps}
        quickFilterProps={quickFilterProps}
        viewOptionsProps={viewOptionsProps}
        totalCount={sortedRecords.length}
        selectedCount={resourceState.selectedIds.size}
        countTotal={labels.countTotal}
        collapseToggle={collapseToggle}
        selectionBar={
          <ResourceControlsSelectionBar
            selectedCount={resourceState.selectedIds.size}
            companies={companies}
            companyMetadata={companyMetadata}
            getPermitTypeById={permitTypesStore.getDisplayTypeByAnyVersionId}
            allWorkflowTypeIds={allWorkflowTypeIds}
            projectDateStore={projectDateStore}
            resourceTypeId={meta.resourceTypeId}
            onBulkWorkflowTypes={bulkSaveWorkflowTypes}
            onBulkPermittedCompanies={bulkSavePermittedCompanies}
            onBulkOperatingIntervals={bulkSaveOperatingIntervals}
            onEditProjectHours={onEditProjectHours}
            onBulkClosures={bulkSaveClosures}
            getDefaultClosures={getDefaultClosures}
            onBulkRestrictions={bulkSaveRestrictions}
            onBulkBookable={bulkSaveBookable}
            onBulkShowInMap={bulkSaveShowInMap}
            onDelete={deleteSelected}
            confirmDelete={labels.confirmDelete}
            controls={controls}
            hasProjectLocationSelected={hasProjectLocationSelected}
          />
        }
        onCreateNew={actions.createNew}
      />
      <div className="tw:min-h-0 tw:flex-1">
        {meta.isLoading ? (
          <div className="tw:flex tw:h-full tw:items-center tw:justify-center">
            <Spinner size="md" />
          </div>
        ) : (
          <ResourceControlsTable
            records={sortedRecords}
            columns={visibleColumns}
            grouping={currentGrouping}
            groupingHierarchy={groupingHierarchy}
            resourceTypeId={meta.resourceTypeId}
            projectClosedIntervals={projectClosedIntervals}
            collapsedCategories={groupingControls.collapsedCategories}
            toggleCategory={groupingControls.toggleCategory}
            companies={companies}
            companyMetadata={companyMetadata}
            getCompanyById={companiesStore.getCompanyById}
            getPermitTypeById={permitTypesStore.getDisplayTypeByAnyVersionId}
            allWorkflowTypeIds={allWorkflowTypeIds}
            selectedSortingOptionId={
              groupSortProps.selectedSortingOptionId ?? null
            }
            selectedSortOrder={groupSortProps.selectedSortOrder}
            onSortingChange={groupSortProps.onSortingChange}
            selectedIds={resourceState.selectedIds}
            onToggleSelection={actions.toggleSelection}
            onSetSelection={actions.setSelection}
            onSelectRecord={actions.selectRecord}
            onDeleteRecord={deleteRecord}
            onSavePermittedCompanies={handleSavePermittedCompanies}
            onEnableBookable={handleEnableBookable}
            onSaveBookable={handleSaveBookable}
            onSaveShowInMap={handleSaveShowInMap}
            onSaveWorkflowRestrictions={handleSaveWorkflowRestrictions}
            onDeleteWorkflowRestrictions={handleDeleteWorkflowRestrictions}
            onSaveOperatingIntervals={handleSaveOperatingIntervals}
            onEditProjectHours={onEditProjectHours}
            closuresStore={closuresStore}
            projectDateStore={projectDateStore}
            onSaveClosure={handleSaveClosure}
            onRemoveClosure={removeClosure}
            confirmDelete={labels.confirmDelete}
            ariaLabel={ariaLabel}
            fieldProjector={meta.fieldProjector}
            controls={controls}
          />
        )}
      </div>
      <ResourceControlCascadeConfirmDialog
        isOpen={!!cascadeDialog}
        cascade={cascadeDialog?.cascade ?? null}
        isCommitting={isCommittingCascade}
        onConfirm={onConfirmCascade}
        onCancel={onCancelCascade}
      />
    </div>
  )
}

export default observer(ResourceControlsContent)
